import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5core from '@amcharts/amcharts5';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import {
  createCursorBullet,
  createTooltip,
  customizeAxisLabels,
  setupCursor,
  setupCursorEvents,
} from '../../utilities/chartHelpers';

export function createSplitWellChart({
  chartDiv,
  config,
  wellTestData,
  startDate,
  endDate,
  showTooltip,
  addAmChartRef,
  idx,
  syncCursor,
  syncAxes,
  isFullscreen = false,
}: {
  chartDiv: HTMLElement;
  config: any;
  wellTestData: any[];
  startDate: string | undefined;
  endDate: string | undefined;
  showTooltip: boolean;
  addAmChartRef?: (idx: number, chart: any) => void;
  idx?: number;
  syncCursor?: (...args: any[]) => void;
  syncAxes?: (...args: any[]) => void;
  isFullscreen?: boolean;
}) {
  // Remove previous chart root if exists
  if ((chartDiv as any)['__am5Root']) {
    try {
      ((chartDiv as any)['__am5Root'] as am5.Root).dispose();
    } catch (e) {
      console.error('Error disposing previous chart root:', e);
    }
  }

  const root = am5.Root.new(chartDiv);
  (chartDiv as any)['__am5Root'] = root;

  root.setThemes([am5themes_Dark.new(root)]);

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      layout: root.verticalLayout,
      paddingLeft: 0,
      paddingRight: 30,
      paddingTop: 20,
      paddingBottom: 0,
      background: am5.Rectangle.new(root, {
        fill: am5.color('#122031'),
        fillOpacity: 0,
      }),
    }),
  );

  if (addAmChartRef && typeof idx === 'number' && !isFullscreen) {
    addAmChartRef(idx, chart);
  }

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: 'minute', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: 80,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
      }),
    }),
  );

  if (startDate && endDate) {
    xAxis.setAll({
      min: new Date(startDate).getTime(),
      max: new Date(endDate).getTime(),
      strictMinMax: true,
    });
  }

  xAxis.get('renderer').labels.template.setAll({
    fill: am5.color('#f7f9f9'),
    fontSize: 12,
    fontFamily: 'Mulish',
  });

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      min: 0,
      renderer: am5xy.AxisRendererY.new(root, {
        pan: 'zoom',
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
      }),
    }),
  );

  yAxis.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      text: config.units && config.units !== '' ? `${config.name} (${config.units})` : `${config.name}`,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 16,
      fontFamily: 'Mulish',
      fontWeight: '400',
      marginRight: -10,
      textAlign: 'center',
      maxHeight: 180,
      oversizedBehavior: 'wrap',
    }),
  );

  customizeAxisLabels(yAxis.get('renderer'), 14);

  yAxis.get('renderer').labels.template.setAll({
    fill: am5.color('#f7f9f9'),
    fontSize: 12,
    fontFamily: 'Mulish',
    paddingRight: 10,
  });

  const gridSettings = {
    stroke: am5.color('#616a77'),
    strokeWidth: 1,
    strokeOpacity: 0.5,
    visible: true,
  };

  yAxis.get('renderer').grid.template.setAll(gridSettings);
  xAxis.get('renderer').grid.template.setAll(gridSettings);

  const tooltip =
    showTooltip && createTooltip
      ? createTooltip('', root, {
          backgroundColor: '#001023',
          backgroundOpacity: 0.6,
          strokeColor: '#4A5463',
          textColor: '#FFFFFF',
          fontSize: 14,
          verticalPosition: 'top',
          textAlign: 'center',
        })
      : undefined;

  if (tooltip) {
    tooltip.adapters.add('labelText', (_text, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) return '';
      const dataContext = dataItem.dataContext as any;
      if (!dataContext) return '';
      const value = dataContext[config.field];
      const unitText = config.units && config.units !== '' ? ` (${config.units})` : '';
      return `${config.name}${unitText} ${value} {valueX.formatDate("MMM d, yyyy")}`;
    });
  }

  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: config.name,
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: config.field,
      valueXField: 'Timestamp',
      stroke: am5.color(config.testColor),
      fill: am5.color(config.testColor),
      tooltip: tooltip,
      visible: config.splitView,
    }),
  );

  series.strokes.template.setAll({ strokeWidth: 2 });

  const bullet = createCursorBullet(root, config.testColor);
  series.bulletsContainer.children.push(bullet.get('sprite'));

  series.data.setAll(wellTestData);

  // Cursor
  const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2, yAxis);
  setupCursorEvents(cursor, xAxis, chart, [series], [bullet], [], [], 'line', {
    syncCursor: !isFullscreen ? syncCursor : undefined,
    showTooltip,
  });

  // Sync axes on zoom/pan
  if (!isFullscreen && syncAxes) {
    xAxis.on('start', function () {
      syncAxes(chart);
    });
    xAxis.on('end', function () {
      syncAxes(chart);
    });
  }

  chart.appear(1000, 100);

  return root;
}
